import { store } from '../store';
import { addTranscript, addSpeechRateAlert, updateCurrentSpeechRate } from '../store/conversationSlice';
import { websocketService } from './websocketService';
import { landmineWebSocketService } from './landmineWebSocketService';
import { competitorWebSocketService } from './competitorWebSocketService';
import { sessionManager } from './sessionManager';
import { validateAndRefreshToken } from '../utils/tokenUtils';

interface RecallTranscript {
  type: string;
  bot_id?: string;
  data?: {
    text?: string;
    speaker?: string;
    speaker_id?: string;
    is_host?: boolean;
    timestamp?: string;
    // Speech rate fields
    total_words?: number;
    max_words?: number;
    time_window?: number;
    words_per_minute?: number;
    is_too_fast?: boolean;
  };
  error?: string;
}

class RecallWebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private isConnecting = false;
  private botId: string | null = null;
  private websocketUrl: string | null = null;

  async connect(botId: string, websocketUrl: string): Promise<void> {
    // Check if we're switching to a different bot
    const isSwitchingBots = this.botId && this.botId !== botId;

    if (isSwitchingBots) {
      console.log(`🔄 Switching Recall WebSocket from bot ${this.botId} to ${botId}`);
      // Force disconnect from old bot before connecting to new one
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
      this.isConnecting = false; // Reset connecting flag to allow new connection
    }

    // Guard: Check if already connected to SAME bot to prevent duplicate connections
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN && this.botId === botId)) {
      console.log('🔌 Already connected or connecting to Recall WebSocket for same bot');
      return;
    }

    // Clean up any existing connection before creating new one
    if (this.ws) {
      console.log('🔌 Closing existing Recall WebSocket before reconnecting');
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = true;
    this.botId = botId;
    this.websocketUrl = websocketUrl;

    try {
      // Get room_id from sessionManager
      const roomId = sessionManager.getRoomId() || botId;

      // Always pull a fresh Firebase token at (re)connect time. Reading from
      // Redux is unsafe — Redux can lag onIdTokenChanged, and reconnects
      // during long sessions are exactly when a stale token would surface.
      const { token } = await validateAndRefreshToken();

      if (!token) {
        console.error('🔌 No Firebase token available for Recall WebSocket authentication');
        this.isConnecting = false;
        throw new Error('Authentication token required');
      }

      // Connect to the recall WebSocket service with room_id and JWT token
      const wsUrl = `${websocketUrl}?room_id=${roomId}&token=${encodeURIComponent(token)}`;
      console.log('🔌 Connecting to Recall WebSocket with authentication');

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('✅ Connected to Recall WebSocket service');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
      };

      this.ws.onmessage = (event) => {
        try {
          const message: RecallTranscript = JSON.parse(event.data);
          console.log('📨 Received WebSocket message:', message);
          this.handleMessage(message);
        } catch (error) {
          console.error('❌ Error parsing Recall WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ Recall WebSocket error:', error);
      };

      this.ws.onclose = () => {
        console.log('🔌 Recall WebSocket connection closed');
        this.isConnecting = false;
        this.ws = null;

        // Attempt to reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnect();
        }
      };

    } catch (error) {
      console.error('❌ Failed to connect to Recall WebSocket:', error);
      this.isConnecting = false;
      throw error;
    }
  }

  private handleMessage(message: RecallTranscript) {
    // Skip connection established messages
    if (message.type === 'connection_established') {
      console.log('✅ Recall WebSocket connection confirmed');
      return;
    }

    // Check if message is for the currently active bot
    // If message has bot_id, verify it matches our connected bot
    if (message.bot_id && message.bot_id !== this.botId) {
      console.log(`⏭️ Ignoring message for different bot: ${message.bot_id} (active: ${this.botId})`);
      return;
    }

    // Also check against sessionManager's room_id for extra safety
    const activeRoomId = sessionManager.getRoomId();
    if (activeRoomId && message.bot_id && message.bot_id !== activeRoomId) {
      console.log(`⏭️ Ignoring message - bot_id ${message.bot_id} doesn't match active room ${activeRoomId}`);
      return;
    }

    // Handle participant join event - bot has joined the meeting
    if (message.type === 'participant_join') {
      console.log('🤖 Bot has joined the meeting');

      // Set call start time in session manager
      sessionManager.setCallStartTime();

      // Dispatch custom event that the modal can listen for
      window.dispatchEvent(new CustomEvent('bot-joined-meeting', {
        detail: { botId: this.botId }
      }));
      return;
    }

    // Handle transcripts from Recall.ai
    if (message.type === 'transcript') {

      if (!message.data) {
        console.warn('⚠️ Transcript message has no data field');
        return;
      }

      const { text, speaker, is_host, timestamp } = message.data;

      if (!text || !text.trim()) {
        console.log('⏭️ Skipping empty transcript');
        return; // Skip empty transcripts
      }

      // Trust the backend's pre-calculated speaker role
      // The backend uses participant_id caching to prevent Recall.ai is_host inconsistencies
      let actualSpeaker: 'customer' | 'salesperson';

      if (speaker === 'customer' || speaker === 'salesperson') {
        // Use backend's cached speaker role (handles is_host inconsistencies)
        actualSpeaker = speaker;
      } else {
        // Fallback if backend sends unexpected value (shouldn't happen)
        console.warn(`⚠️ Unexpected speaker value from backend: ${speaker}, using is_host fallback`);
        const state = store.getState();
        const isSalespersonHost = state.conversation.isSalespersonHost;
        actualSpeaker = isSalespersonHost
          ? (is_host ? 'salesperson' : 'customer')
          : (is_host ? 'customer' : 'salesperson');
      }

      console.log(`📝 Recall transcript - Backend speaker: ${speaker}, Using: ${actualSpeaker}`);

      // Add transcript to Redux store for display
      store.dispatch(addTranscript({
        text: text,
        speaker: actualSpeaker,
        timestamp: timestamp || new Date().toISOString()
      }));

      // NOTE: Frontend no longer sends transcripts to backend services!
      // All transcript broadcasting is handled by recall_websocket_service.py (port 3456)
      // which receives transcripts from Recall.ai and forwards them to:
      //   - production_final_server.py (questions/answers)
      //   - landmine_detection_service.py (landmine detection)
      //   - competitor_detection_service.py (competitor mentions)
      //   - saver_service.py (incremental persistence)
      //
      // Frontend only:
      //   1. Receives transcripts from recall_websocket_service.py for display
      //   2. Receives processed results from backend services (questions, alerts, etc.)
      return;
    }

    // Handle speech rate updates
    if (message.type === 'speech_rate_update') {
      store.dispatch(updateCurrentSpeechRate({
        words_per_minute: message.data?.words_per_minute || 0,
        is_too_fast: message.data?.is_too_fast || false,
      }));
      return;
    }

    // Handle speech rate alerts
    if (message.type === 'speech_rate_alert') {
      store.dispatch(addSpeechRateAlert({
        total_words: message.data?.total_words || 0,
        max_words: message.data?.max_words || 0,
        time_window: message.data?.time_window || 0,
        words_per_minute: message.data?.words_per_minute || 0,
        timestamp: new Date().toISOString(),
      }));
      // Also update current speech rate when we get an alert
      store.dispatch(updateCurrentSpeechRate({
        words_per_minute: message.data?.words_per_minute || 0,
        is_too_fast: true,
      }));
      console.log('🎤 Speech rate alert received:', message.data);
      return;
    }
  }

  private async reconnect() {
    if (!this.botId || !this.websocketUrl) {
      console.log('⚠️ Cannot reconnect - missing bot ID or WebSocket URL');
      return;
    }

    this.reconnectAttempts++;
    console.log(`🔄 Attempting to reconnect to Recall WebSocket (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      this.connect(this.botId!, this.websocketUrl!);
    }, this.reconnectDelay);

    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 10000);
  }

  disconnect() {
    if (this.ws) {
      console.log('🔌 Disconnecting from Recall WebSocket');
      this.ws.close();
      this.ws = null;
    }
    this.botId = null;
    this.websocketUrl = null;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Send a message to the recall WebSocket service
   * Used for forwarding events to saver_service (e.g., sales playbook updates)
   */
  send(message: { type: string; data: any }): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      console.log(`📤 Sent message to recall WebSocket: ${message.type}`);
    } else {
      console.warn('⚠️ Cannot send message - recall WebSocket not connected');
    }
  }

  /**
   * Send a sales playbook update event to be forwarded to saver_service
   * @deprecated Use sendSalesPlaybookUpdate instead
   */
  sendTalkTrackUpdate(data: {
    roomId: string;
    updateType: 'selection' | 'question_asked' | 'question_note';
    industry?: string;
    industryDisplayName?: string;
    role?: string;
    roleDisplayName?: string;
    totalQuestionsAvailable?: number;
    questionId?: string;
    questionText?: string;
    tags?: string[];
    isAsked?: boolean;
    note?: string;
  }): void {
    // Forward to the new method
    this.sendSalesPlaybookUpdate(data);
  }

  /**
   * Send a sales playbook update event to be forwarded to saver_service
   */
  sendSalesPlaybookUpdate(data: {
    roomId: string;
    updateType: 'selection' | 'question_asked' | 'question_note' | 'pitch_checked' | 'pitch_note' | 'advanced_checked' | 'advanced_note';
    industry?: string;
    industryDisplayName?: string;
    role?: string;
    roleDisplayName?: string;
    totalQuestionsAvailable?: number;
    questionId?: string;
    questionText?: string;
    tags?: string[];
    isAsked?: boolean;
    isChecked?: boolean;
    note?: string;
    pitchId?: string;
    pitchText?: string;
    advancedId?: string;
    advancedText?: string;
  }): void {
    this.send({
      type: 'sales_playbook_update',
      data: {
        room_id: data.roomId,
        update_type: data.updateType,
        industry: data.industry,
        industryDisplayName: data.industryDisplayName,
        role: data.role,
        roleDisplayName: data.roleDisplayName,
        totalQuestionsAvailable: data.totalQuestionsAvailable,
        questionId: data.questionId,
        questionText: data.questionText,
        tags: data.tags,
        isAsked: data.isAsked,
        isChecked: data.isChecked,
        note: data.note,
        pitchId: data.pitchId,
        pitchText: data.pitchText,
        advancedId: data.advancedId,
        advancedText: data.advancedText
      }
    });
  }
}

export const recallWebSocketService = new RecallWebSocketService();